import ActionFeedBackModal from '@/components/ActionFeedbackModal';
import Constants from '@/Constants';
import Category, { CategoryRequest } from '@/models/Category';
import { ApplicationState } from '@/store';
import { getCategoriesRequest, toggleCategory } from '@/store/category/action';
import {
    batchFavoriteClear,
    batchFavoriteRequest,
    getFavoriteListRequest,
    removeCategoryFavoriteRequest,
    setCategoryFavoriteRequest,
} from '@/store/favorite/actions';
import { Favorite } from '@/store/favorite/types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../Loader';
import './style.scss';

interface Props {
    saveOnSelect: boolean;
    backgroundColorTransparent?: boolean;
}

const CheckCategories: React.FC<Props> = (props: Props) => {
    const { saveOnSelect, backgroundColorTransparent } = props;

    const {
        organizationId,
        selectedCategories,
        categories,
        loading,
        strings,
        favoriteList,
        favoriteLoading,
        successOnSaving,
        hasErrorOnSaving,
    } = useSelector((state: ApplicationState) => ({
        organizationId: state.identity.data.user && state.identity.data.user.orgId,
        categories: state.categories.data.categories,
        selectedCategories: state.categories.data.selectedCategories,
        loading: state.categories.loading,
        strings: state.locale.data,
        favoriteList: state.favorite.favoriteList,
        favoriteLoading: state.favorite.loading,
        successOnSaving: state.favorite.success,
        hasErrorOnSaving: state.favorite.hasError,
    }));

    const dispatch = useDispatch();
    const [disableSave, setDisableSave] = useState<boolean>(false);
    const [favoriteCategoryList, setFavoriteCategoryList] = useState<Favorite[]>([]);

    useEffect(() => {
        return () => {
            dispatch(batchFavoriteClear());
        };
    }, [dispatch]);

    useEffect(() => {
        if (favoriteList.length === 0) {
            dispatch(getFavoriteListRequest(false));
        }
        setFavoriteCategoryList(favoriteList.filter((favorite: Favorite) => favorite.contentType === Constants.entityTypes.CATEGORY));
    }, [favoriteList.length, dispatch]);

    useEffect(() => {
        favoriteCategoryList
            .filter(
                (favoriteCategory: Favorite) =>
                    selectedCategories.length === 0 || !selectedCategories.find((category: Category) => favoriteCategory.contentId === category.id),
            )
            .forEach((favoriteCategory: Favorite) => {
                dispatch(toggleCategory({ id: favoriteCategory.contentId } as Category));
            });
    }, [favoriteCategoryList.length, dispatch]);

    useEffect(() => {
        dispatch(getCategoriesRequest({ applicationId: Constants.APP_ID, orgId: organizationId } as CategoryRequest));
    }, [dispatch, organizationId]);

    useEffect(() => {
        if (!saveOnSelect) {
            const unsavedCategories = selectedCategories.filter(
                (category: Category) => !favoriteCategoryList.find((favoriteCategory: Favorite) => category.id === favoriteCategory.contentId),
            );

            setDisableSave(
                !favoriteLoading &&
                    ((selectedCategories.length > 0 && selectedCategories.length !== favoriteCategoryList.length) || unsavedCategories.length > 0),
            );
        }
    }, [selectedCategories.length, favoriteCategoryList, favoriteLoading, dispatch, saveOnSelect]);

    const categoryIsSelected = (category: Category): boolean =>
        selectedCategories && !!selectedCategories.find((categorySelected: Category) => categorySelected.id === category.id);

    const selectCategory = (category: Category) => {
        dispatch(toggleCategory(category));

        if (saveOnSelect) {
            const categoryIndex = selectedCategories.findIndex((savedCategory: Category) => savedCategory.id === category.id);

            if (categoryIndex > -1) {
                dispatch(removeCategoryFavoriteRequest({ contentId: category.id } as Favorite));
            } else {
                dispatch(setCategoryFavoriteRequest({ contentId: category.id } as Favorite));
            }
        }
    };

    const saveBatchFavorites = () => {
        dispatch(
            batchFavoriteRequest({
                entityType: Constants.entityTypes.CATEGORY,
                favoriteIds: selectedCategories.map((category: Category) => category.id || 0),
            }),
        );
    };

    const handleOnSave = (): void => {
        setDisableSave(false);
        saveBatchFavorites();
        dispatch(batchFavoriteClear());
    };

    return (
        <>
            {!saveOnSelect && (
                <ActionFeedBackModal
                    error={hasErrorOnSaving}
                    errorMessage={strings.ERROR_WHILE_SAVE_DATA}
                    loading={loading}
                    onRetry={() => saveBatchFavorites()}
                    success={successOnSaving}
                    successMessage={strings.INTERESTS_EDITION_SAVE_SUCCESS}
                />
            )}
            <div className="categories-container">
                {loading && <Loader />}
                <div className="categories-wrapper">
                    {!loading &&
                        categories &&
                        categories.map((category: Category) => {
                            const transparentClass = backgroundColorTransparent ? 'transparent' : '';
                            const selectedClass = categoryIsSelected(category) ? 'selected' : '';

                            return (
                                <div
                                    className={`select-category  ${!!selectedClass ? selectedClass : transparentClass}`}
                                    key={category.id}
                                    onClick={() => selectCategory(category)}
                                >
                                    <span className={`category-name ${selectedClass}`}>{category.name.toUpperCase()}</span>
                                </div>
                            );
                        })}
                </div>
                {categories.length > 0 && !saveOnSelect && (
                    <button className="btn btn-primary" disabled={!disableSave} onClick={handleOnSave}>
                        {strings.SAVE}
                    </button>
                )}
            </div>
        </>
    );
};

export default CheckCategories;
